import { getFile } from '../api/data-connectors/get-file'
import { queryClient } from '../clients/react-query-client'

const FAST_REFETCH_INTERVAL_MS = 3000
const SLOW_REFETCH_INTERVAL_MS = 15000
const FAST_POLL_WINDOW_MS = 60000
const POLL_IDLE_TIMEOUT_MS = 5 * 60 * 1000
const POLL_ABSOLUTE_TIMEOUT_MS = 60 * 60 * 1000

interface PollingState {
  startTimeMs: number
  lastSuccessfulPollMs: number
  fileName: string
}

// Singleton: continues polling independently of component lifecycle
class FileStatusService {
  private pollingFiles: Map<string, PollingState> = new Map()

  private getNextRefetchIntervalMs(startTimeMs: number): number {
    const elapsedMs = Date.now() - startTimeMs
    return elapsedMs < FAST_POLL_WINDOW_MS ? FAST_REFETCH_INTERVAL_MS : SLOW_REFETCH_INTERVAL_MS
  }

  private stopWithFailure(
    fileId: string,
    onComplete: () => void,
    notify: (params: { isSuccess: boolean; text: string; position: 'top' | 'bottom' }) => void,
    t: (key: string, options?: Record<string, string>) => string
  ): void {
    const fileName = this.pollingFiles.get(fileId)?.fileName ?? ''
    this.pollingFiles.delete(fileId)
    onComplete()
    notify({
      isSuccess: false,
      text: t('files.uploadFailureToast', { filename: fileName || t('files.file') }),
      position: 'bottom',
    })
  }

  startPolling(
    fileId: string,
    onComplete: () => void,
    notify: (params: { isSuccess: boolean; text: string; position: 'top' | 'bottom' }) => void,
    t: (key: string, options?: Record<string, string>) => string
  ): void {
    if (this.pollingFiles.has(fileId)) {
      return
    }

    const now = Date.now()
    this.pollingFiles.set(fileId, {
      startTimeMs: now,
      lastSuccessfulPollMs: now,
      fileName: '',
    })
    this.pollStatus(fileId, onComplete, notify, t)
  }

  private async pollStatus(
    fileId: string,
    onComplete: () => void,
    notify: (params: { isSuccess: boolean; text: string; position: 'top' | 'bottom' }) => void,
    t: (key: string, options?: Record<string, string>) => string
  ): Promise<void> {
    const state = this.pollingFiles.get(fileId)
    if (!state) return

    const now = Date.now()
    if (now - state.startTimeMs >= POLL_ABSOLUTE_TIMEOUT_MS) {
      console.error(`Polling absolute timeout reached for file ${fileId}`)
      this.stopWithFailure(fileId, onComplete, notify, t)
      return
    }

    if (now - state.lastSuccessfulPollMs >= POLL_IDLE_TIMEOUT_MS) {
      console.error(`Polling idle timeout reached for file ${fileId}`)
      this.stopWithFailure(fileId, onComplete, notify, t)
      return
    }

    try {
      const data = await queryClient.fetchQuery({
        queryKey: ['getFile', fileId],
        queryFn: () => getFile(fileId),
        staleTime: 0,
      })
      state.fileName = data.name || state.fileName
      state.lastSuccessfulPollMs = Date.now()

      const status = data?.parsingStatus
      if (status === 'processing' || status === 'pending') {
        const nextIntervalMs = this.getNextRefetchIntervalMs(state.startTimeMs)
        setTimeout(() => this.pollStatus(fileId, onComplete, notify, t), nextIntervalMs)
        return
      }

      if (status === 'failed' || status === 'unknown') {
        if (data.errorMessage) {
          console.error(`Polling stopped for file ${fileId}: ${data.errorMessage}`)
        }
        this.stopWithFailure(fileId, onComplete, notify, t)
        return
      }

      if (status === 'completed') {
        this.pollingFiles.delete(fileId)
        await queryClient.invalidateQueries({ queryKey: ['filepool'] })
        onComplete()
        notify({
          isSuccess: true,
          text: t('files.uploadSuccessToast', { filename: state.fileName || t('files.file') }),
          position: 'bottom',
        })
        return
      }

      console.error(`Polling returned unsupported status for file ${fileId}: ${String(status)}`)
      this.stopWithFailure(fileId, onComplete, notify, t)
    } catch (error) {
      console.error(`Polling failed for file ${fileId}:`, error)
      const nextIntervalMs = this.getNextRefetchIntervalMs(state.startTimeMs)
      setTimeout(() => this.pollStatus(fileId, onComplete, notify, t), nextIntervalMs)
    }
  }
}

export const fileStatusService = new FileStatusService()
